import { Building2 } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { useAdvisorFirmRecheck } from './useAdvisorFirm';

/**
 * What `/` renders for someone standing in an advisory FIRM workspace whose own advisor
 * relationship (`GET /advisor/firms`'s `advisorFor`) has not resolved yet — reached via
 * `HomeRoute`'s `workspaceKind === 'firm'` branch, the same signal `Layout.tsx` now gates
 * the family-pillar suppression on directly (steward-app#1271).
 *
 * ⚠️ **What causes `advisorFor` to be transiently null here is NOT established, and this
 * component does not claim to know.** An earlier version of this docblock framed the state
 * as a deployment rollout window — the façade catching up with a directory-only firm's
 * configuration, in the same window the prod runbook mounts the pack before the firm's own
 * configuration lands. Neither explanation survives the occurrences actually observed: the
 * founder hit this twice, on 2026-09-07 and 2026-09-08, once console-creating a brand-new
 * firm in dev, and once onboarding a SECOND advisor into RIPL Wealth in prod — a firm that
 * had been live since 2026-08-28, whose first advisor had been an active member throughout.
 * Neither a directory-publish window nor a first-deploy configuration gap explains that
 * second occurrence. steward-app#1271's comments have the full account. The honest summary
 * is: this state is reachable by ANY advisor whose `advisorFor` has not resolved yet, for a
 * reason still under investigation, and it is emphatically **not** confined to brand-new or
 * directory-only firms — do not narrow the copy or the recheck behaviour below back to that
 * assumption, and do not invent a cause here that the issue has not established.
 *
 * ⚠️ **What this replaces is the FAMILY DASHBOARD, and that is the whole point.** `HomeRoute`
 * used to fall through to `<Dashboard />` here — a net-worth hero, an allocation strip and a
 * "largest holdings" list over a workspace that holds no family's money, assembled from six
 * queries fired against it. steward-app#1212 made this component the reason that no longer
 * happens on this path.
 *
 * Deliberately NOT a redirect to `/advisor`. `RequireAdvisorFirm` renders "This area is for
 * advisory-firm team members" for a null `advisorFor`, and telling a firm's own staff that
 * the advisor area is not for them is a worse answer than telling them it is not ready. The
 * redirect stays what it always was: for a caller the server HAS placed at a firm.
 *
 * **Auto-recheck (steward-app#1271).** The copy below promises the page updates on its own;
 * {@link useAdvisorFirmRecheck} is what makes that claim true — see its own header in
 * `useAdvisorFirm.ts` for the interval, why it is scoped to this one call site, and why it
 * never widens into a poll for the query's other observers. "Check again" is the impatient
 * advisor's way out of waiting on the interval; it shows it is working (the button's label
 * and disabled state) and never claims the recheck succeeded — succeeding is not this
 * component's fact to announce, it is whatever `HomeRoute` renders next once `advisorFor`
 * (or `firmChoice`) actually answers.
 *
 * Copy: says where they are, why the page is empty, and what to expect next. It never
 * claims something is broken and never promises a time (CLAUDE.md's content rules — no
 * em/en dashes, warm and plain).
 */
export function FirmWorkspaceNotReady() {
  const { isRechecking, recheck } = useAdvisorFirmRecheck();

  return (
    <EmptyState
      icon={Building2}
      data-testid="firm-workspace-not-ready"
      message={
        'This is your firm’s workspace rather than a client’s, so the pages here are empty. '
        + 'We are still setting up your access. This page will update on its own '
        + 'when your advisor portal is ready, and you do not need to do anything.'
      }
      action={{
        label: isRechecking ? 'Checking…' : 'Check again',
        onAction: () => { void recheck(); },
        disabled: isRechecking,
      }}
      secondaryAction={{ label: 'Send us a note', to: '/feedback' }}
    />
  );
}
